import React, { useState, useCallback, useMemo } from "react";
import {
  ExternalLink,
  TrendingUp,
  BarChart3,
  SlidersHorizontal,
  Check,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useParams } from "@tanstack/react-router";
import * as Popover from "@radix-ui/react-popover";
import { useTranslation } from "@/packages/utils";
import { useWorkspaceQuery } from "@/hooks/use-workspace-query";
import { useWorkspace } from "@/routes/$workspaceSlug/route";
import { api } from "convex/_generated/api";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  XAxis,
  YAxis,
  Customized,
} from "recharts";
import {
  ChartContainer,
  ChartTooltip,
  type ChartConfig,
} from "@/components/ui/chart";

import { getPaletteColor } from "@/lib/color-palettes";

const SEVERITY_SLOT: Record<string, number> = {
  critical: 1,
  major: 2,
  minor: 3,
  observation: 4,
};

function getSeverityColor(severity: string): string {
  return getPaletteColor("findings", SEVERITY_SLOT[severity] ?? 4);
}

/** Finding-status badge colors: open=deadlines-1 (red), draft=fallback grey, resolved=deadlines-3 (green) */
function getStatusBadgeColor(status: string): string {
  if (status === "open") return getPaletteColor("deadlines", 1);
  if (status === "resolved") return getPaletteColor("deadlines", 3);
  return getPaletteColor("audit-status", 6); // archived/grey slot for draft
}

type Severity = "critical" | "major" | "minor" | "observation";
type TimeRange = "90d" | "30d" | "7d";
type AuditScope = "active" | "all" | "completed";

const SEVERITIES: Severity[] = ["critical", "major", "minor", "observation"];

type NewFinding = { _id: string; severity: string; title: string };

function FindingRow({
  finding,
  workspaceSlug,
  isHighlighted,
}: {
  finding: {
    _id: string;
    title: string;
    status: string;
    auditId: string;
    auditName: string;
    categoryName: string;
  };
  workspaceSlug: string | undefined;
  isHighlighted?: boolean;
}) {
  const statusColor = getStatusBadgeColor(finding.status);

  return (
    <div
      data-finding-id={finding._id}
      className={`flex items-center justify-between py-1.5 px-3 bg-surface rounded-lg mb-1 hover:bg-surface/80 transition-all ${isHighlighted ? "ring-2 ring-accent/50 bg-accent/5" : ""}`}
    >
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium text-primary truncate">
          {finding.title}
        </div>
        <div className="flex items-center gap-1.5 min-w-0">
          <span className="text-xs text-muted truncate">
            {finding.auditName}
          </span>
          {finding.categoryName && (
            <span className="text-[11px] font-medium px-1.5 rounded-md bg-card border border-border text-secondary whitespace-nowrap shrink-0">
              {finding.categoryName}
            </span>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0 ml-3">
        <span
          className="text-[11px] font-semibold px-1.5 py-0.5 rounded-full whitespace-nowrap"
          style={{
            color: statusColor,
            background: `${statusColor}15`,
          }}
        >
          {finding.status}
        </span>
        {workspaceSlug && (
          <Link
            to="/$workspaceSlug/audits/$auditId"
            params={{
              workspaceSlug: workspaceSlug as string,
              auditId: finding.auditId,
            }}
            search={{
              tab: "findings" as const,
            }}
            className="text-muted hover:text-secondary transition-colors shrink-0"
            title="Go to finding"
            onClick={(e) => e.stopPropagation()}
          >
            <ExternalLink className="h-3.5 w-3.5" />
          </Link>
        )}
      </div>
    </div>
  );
}

function EmptyChartPlaceholder({ label }: { label: string }) {
  return (
    <div className="flex-1 min-h-0 flex flex-col">
      <div className="relative flex-1 min-h-[120px]">
        {/* Y-axis gridlines */}
        <div className="absolute inset-0 flex flex-col justify-between pointer-events-none">
          {[0, 1, 2, 3, 4].map((i) => (
            <div key={i} className="flex items-center gap-2 w-full">
              <span className="text-[10px] text-muted/40 w-4 text-right tabular-nums shrink-0">
                {(4 - i) * 5}
              </span>
              <div className="flex-1 border-t border-dashed border-border/50" />
            </div>
          ))}
        </div>
        {/* Empty bar placeholders */}
        <div className="absolute bottom-0 left-8 right-0 flex items-end justify-around h-[85%] px-2">
          {SEVERITIES.map((sev, i) => (
            <div
              key={sev}
              className="w-7 rounded-t-sm opacity-15"
              style={{
                backgroundColor: getSeverityColor(sev),
                height: `${[18, 14, 22, 10][i]}%`,
              }}
            />
          ))}
        </div>
        {/* Centered label */}
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-sm text-muted bg-card/80 px-3 py-1 rounded-md">
            {label}
          </span>
        </div>
      </div>
      {/* X-axis line */}
      <div className="ml-8 border-t border-border/60 mt-1" />
    </div>
  );
}

function SkeletonChart() {
  return (
    <div className="space-y-3">
      <div className="h-[250px] bg-surface rounded-lg animate-pulse" />
      <div className="flex items-center justify-center gap-6">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="flex items-center gap-1.5">
            <div className="w-2.5 h-2.5 rounded-full bg-surface animate-pulse" />
            <div className="w-16 h-3 bg-surface rounded animate-pulse" />
          </div>
        ))}
      </div>
      <div className="flex items-center justify-center gap-4 py-2 border-t border-border">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="w-16 h-4 bg-surface rounded animate-pulse" />
        ))}
      </div>
    </div>
  );
}

export function FindingsSeverity() {
  const { t } = useTranslation(["dashboard"]);
  const { workspaceSlug } = useParams({ strict: false });

  const [scope, setScope] = useState<AuditScope>("active");
  const [timeRange, setTimeRange] = useState<TimeRange>("30d");
  const [chartType, setChartType] = useState<"line" | "bar">("bar");
  const [expandedSeverities, setExpandedSeverities] = useState<Set<string>>(
    () => new Set(),
  );
  const [highlightedFindingId, setHighlightedFindingId] = useState<
    string | null
  >(null);
  const [dotTooltip, setDotTooltip] = useState<{
    x: number;
    y: number;
    findings: NewFinding[];
    severity: Severity;
  } | null>(null);
  // Bar chart: selected date for drill-down
  const [selectedBarDate, setSelectedBarDate] = useState<string | null>(null);
  const chartWrapperRef = React.useRef<HTMLDivElement>(null);
  const scrollContainerRef = React.useRef<HTMLDivElement>(null);

  const isCompact = expandedSeverities.size > 0;

  // Track accent color to re-resolve palette CSS vars when it changes
  const { workspace } = useWorkspace();
  const accentHex = workspace.accentColor ?? "";

  const { data: _timeSeriesData, isLoading: isLoadingTimeSeries } =
    useWorkspaceQuery(api.finding_queries.getTimeSeriesData, {
      scope,
      timeRange,
    });

  const { data: _groupedData, isLoading: isLoadingGrouped } = useWorkspaceQuery(
    api.finding_queries.getBySeverityGrouped,
    { scope, timeRange },
  );

  const timeSeriesData = _timeSeriesData;
  const groupedData = _groupedData;
  const isLoading = isLoadingTimeSeries || isLoadingGrouped;

  // biome-ignore lint/correctness/useExhaustiveDependencies: accentHex triggers re-resolution of CSS-var-based palette colors via getPaletteColor
  const chartConfig: ChartConfig = useMemo(
    () => ({
      critical: {
        label: t("dashboard:findingsSeverity.severity.critical"),
        color: getSeverityColor("critical"),
      },
      major: {
        label: t("dashboard:findingsSeverity.severity.major"),
        color: getSeverityColor("major"),
      },
      minor: {
        label: t("dashboard:findingsSeverity.severity.minor"),
        color: getSeverityColor("minor"),
      },
      observation: {
        label: t("dashboard:findingsSeverity.severity.observation"),
        color: getSeverityColor("observation"),
      },
    }),
    [t, accentHex],
  );

  // Time range filtering now happens in the backend
  const filteredData = useMemo(() => timeSeriesData ?? [], [timeSeriesData]);

  // Convert cumulative data to daily deltas for bar chart
  const barData = useMemo(() => {
    return filteredData.map((point, i) => {
      const prev = i > 0 ? filteredData[i - 1] : null;
      return {
        date: point.date,
        critical: prev ? point.critical - prev.critical : point.critical,
        major: prev ? point.major - prev.major : point.major,
        minor: prev ? point.minor - prev.minor : point.minor,
        observation: prev
          ? point.observation - prev.observation
          : point.observation,
        newFindings: point.newFindings,
      };
    });
  }, [filteredData]);

  // Only render Bar components for severities that have at least one non-zero day
  const activBarSeverities = useMemo(() => {
    return SEVERITIES.filter((sev) => barData.some((d) => d[sev] > 0));
  }, [barData]);

  // Backend already filters by timeRange and scope
  const filteredGroupedData = groupedData ?? undefined;

  const toggleSeverity = useCallback(
    (severity: string) => {
      // In bar mode, legend click shows all dates for that severity — clear date selection
      if (chartType === "bar") {
        setSelectedBarDate(null);
      }
      setExpandedSeverities((prev) => {
        const next = new Set(prev);
        if (next.has(severity)) {
          next.delete(severity);
        } else {
          next.add(severity);
        }
        return next;
      });
    },
    [chartType],
  );

  const scrollToFinding = useCallback((findingId: string) => {
    // Wait for detail list to render/animate, then scroll
    const attemptScroll = () => {
      const container = scrollContainerRef.current;
      if (!container) return;
      const el = container.querySelector(`[data-finding-id="${findingId}"]`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "nearest" });
      }
    };
    // Use a timeout to allow the expand animation to start rendering
    setTimeout(attemptScroll, 100);
  }, []);

  const handleDotClick = useCallback(
    (severity: Severity, findingId: string) => {
      setHighlightedFindingId((prev) =>
        prev === findingId ? null : findingId,
      );
      setExpandedSeverities((prev) => {
        const next = new Set(prev);
        next.add(severity);
        return next;
      });
      scrollToFinding(findingId);
    },
    [scrollToFinding],
  );

  // Bar chart: findings for the selected date + severity filter
  const barSelectedFindings = useMemo(() => {
    if (chartType !== "bar") return null;
    if (!filteredGroupedData) return null;

    // Mode 1: specific date selected (via date label or bar click)
    if (selectedBarDate) {
      const point = barData.find((p) => p.date === selectedBarDate);
      if (!point) return null;
      const dayFindingIds = new Set(point.newFindings.map((f) => f._id));
      if (dayFindingIds.size === 0) return null;

      // Filter to expanded severities only (if a specific bar was clicked, only that severity is expanded)
      const groups = filteredGroupedData.groups
        .filter((g) => expandedSeverities.has(g.severity))
        .map((g) => ({
          ...g,
          findings: g.findings.filter((f) => dayFindingIds.has(f._id)),
        }))
        .filter((g) => g.findings.length > 0);

      return groups.length > 0 ? groups : null;
    }

    // Mode 2: legend click (no specific date, show all findings for expanded severities)
    if (expandedSeverities.size > 0) {
      return filteredGroupedData.groups.filter(
        (g) => expandedSeverities.has(g.severity) && g.findings.length > 0,
      );
    }

    return null;
  }, [
    selectedBarDate,
    chartType,
    barData,
    filteredGroupedData,
    expandedSeverities,
  ]);

  // Click on date label → select all severities for that day
  const handleBarDateClick = useCallback(
    (date: string) => {
      setSelectedBarDate((prev) => {
        if (prev === date) {
          setExpandedSeverities(new Set());
          return null;
        }
        const point = barData.find((p) => p.date === date);
        if (point) {
          const sevsWithData = SEVERITIES.filter((sev) =>
            point.newFindings.some((f) => f.severity === sev),
          );
          setExpandedSeverities(new Set(sevsWithData));
        }
        return date;
      });
    },
    [barData],
  );

  // Click on individual bar → select that severity for that day
  const handleSingleBarClick = useCallback(
    (sev: Severity, entry: { date: string }) => {
      const isSame =
        selectedBarDate === entry.date &&
        expandedSeverities.has(sev) &&
        expandedSeverities.size === 1;
      if (isSame) {
        setSelectedBarDate(null);
        setExpandedSeverities(new Set());
      } else {
        setSelectedBarDate(entry.date);
        setExpandedSeverities(new Set([sev]));
      }
    },
    [selectedBarDate, expandedSeverities],
  );

  const isEmpty = filteredGroupedData && filteredGroupedData.totals.total === 0;

  const activeDetailGroups = filteredGroupedData
    ? SEVERITIES.filter((sev) => expandedSeverities.has(sev))
    : [];

  return (
    <div className="rounded-xl bg-card border border-border h-full flex flex-col">
      {/* Header — pinned */}
      <div className="flex items-center justify-between px-4 pt-3 pb-1 shrink-0">
        <h3 className="text-sm font-semibold text-primary">
          {t("dashboard:findingsSeverity.title")}
        </h3>
        <div className="flex items-center gap-2">
          {/* Chart type toggle */}
          <div className="flex items-center bg-surface border border-border rounded-lg p-0.5">
            <button
              type="button"
              onClick={() => {
                setChartType("line");
                setSelectedBarDate(null);
              }}
              className={`p-1 rounded-md transition-colors ${chartType === "line" ? "bg-card text-primary shadow-sm" : "text-muted hover:text-secondary"}`}
              title="Line chart"
            >
              <TrendingUp className="h-3.5 w-3.5" />
            </button>
            <button
              type="button"
              onClick={() => setChartType("bar")}
              className={`p-1 rounded-md transition-colors ${chartType === "bar" ? "bg-card text-primary shadow-sm" : "text-muted hover:text-secondary"}`}
              title="Bar chart"
            >
              <BarChart3 className="h-3.5 w-3.5" />
            </button>
          </div>

          {/* Time range pill-toggle */}
          <div className="flex items-center rounded-lg bg-surface p-0.5">
            {(["90d", "30d", "7d"] as const).map((range) => (
              <button
                key={range}
                type="button"
                onClick={() => setTimeRange(range)}
                className={`rounded-md px-3 py-1 text-xs font-medium transition-colors ${
                  timeRange === range
                    ? "bg-card-hover text-primary"
                    : "text-muted hover:text-secondary"
                }`}
              >
                {t(
                  `dashboard:findingsSeverity.timeRange.${range}` as "dashboard:findingsSeverity.timeRange.90d",
                )}
              </button>
            ))}
          </div>

          {/* Scope filter icon */}
          <ScopeFilterPopover scope={scope} onScopeChange={setScope} />
        </div>
      </div>

      {/* Chart + Legend */}
      <div className="px-4 flex-1 flex flex-col min-h-0 justify-end">
        {isLoading ? (
          <SkeletonChart />
        ) : isEmpty ? (
          <>
            <EmptyChartPlaceholder
              label={t("dashboard:findingsSeverity.empty")}
            />
            <div className="mt-auto shrink-0">
              <SeverityLegend
                compact={false}
                chartConfig={chartConfig}
                expandedSeverities={expandedSeverities}
                onToggle={toggleSeverity}
                groupedData={filteredGroupedData}
              />
            </div>
          </>
        ) : (
          <>
            {/* Chart + Legend flex container */}
            <div
              className={`flex ${
                isCompact
                  ? "flex-row items-end gap-6"
                  : "flex-col items-center justify-between"
              }`}
            >
              {/* Area Chart wrapper with CSS transition */}
              <div
                ref={chartWrapperRef}
                role="img"
                className={`relative transition-all duration-300 ease-in-out shrink-0 ${
                  isCompact ? "w-3/4 h-[130px]" : "w-full h-[220px]"
                }`}
                onMouseLeave={() => setDotTooltip(null)}
              >
                <ChartContainer
                  config={chartConfig}
                  className="h-full w-full bar-chart-interactive"
                >
                  {chartType === "bar" ? (
                    <BarChart
                      data={barData}
                      barSize={28}
                      barGap={6}
                      barCategoryGap="35%"
                    >
                      <defs>
                        <style>{`
                          @keyframes bar-grow-up {
                            from { transform: scaleY(0); }
                            to { transform: scaleY(1); }
                          }
                          .bar-chart-interactive .recharts-bar-rectangle {
                            transform-origin: bottom;
                            animation: bar-grow-up 0.6s ease-out both;
                          }
                          .bar-chart-interactive .recharts-bar-rectangle:hover {
                            filter: brightness(1.15);
                          }
                          .bar-chart-interactive .recharts-xAxis text {
                            transition: fill 0.15s ease;
                          }
                          .bar-chart-interactive .recharts-xAxis text:hover {
                            fill: var(--color-primary) !important;
                          }
                        `}</style>
                      </defs>
                      <CartesianGrid
                        vertical={false}
                        strokeDasharray="3 3"
                        className="stroke-border"
                      />
                      <XAxis
                        dataKey="date"
                        tickLine={false}
                        axisLine={false}
                        tickMargin={isCompact ? 4 : 8}
                        minTickGap={isCompact ? 60 : 32}
                        className="text-muted"
                        tick={(props: {
                          x: number;
                          y: number;
                          payload: { value: string };
                        }) => {
                          const { x, y, payload } = props;
                          const label = new Date(
                            payload.value,
                          ).toLocaleDateString("de-DE", {
                            month: "short",
                            day: "numeric",
                          });
                          const isSelected = selectedBarDate === payload.value;
                          return (
                            // biome-ignore lint/a11y/noStaticElementInteractions: SVG <text> used as clickable axis tick
                            <text
                              x={x}
                              y={y}
                              textAnchor="middle"
                              dominantBaseline="hanging"
                              fontSize={isCompact ? 9 : 11}
                              fill={
                                isSelected
                                  ? "var(--color-primary)"
                                  : "var(--color-muted)"
                              }
                              fontWeight={isSelected ? 600 : 400}
                              style={{ cursor: "pointer" }}
                              onClick={() => handleBarDateClick(payload.value)}
                            >
                              {label}
                            </text>
                          );
                        }}
                      />
                      <YAxis
                        tickLine={false}
                        axisLine={false}
                        width={isCompact ? 24 : 32}
                        className="text-muted"
                        tick={{ fontSize: isCompact ? 9 : 11 }}
                        allowDecimals={false}
                      />
                      {!isCompact && (
                        <ChartTooltip
                          config={chartConfig}
                          labelFormatter={(value: string) =>
                            new Date(value).toLocaleDateString("de-DE", {
                              month: "short",
                              day: "numeric",
                              year: "numeric",
                            })
                          }
                        />
                      )}
                      {activBarSeverities.map((sev) => (
                        <Bar
                          key={sev}
                          dataKey={sev}
                          fill={getSeverityColor(sev)}
                          radius={[4, 4, 0, 0]}
                          hide={isCompact && !expandedSeverities.has(sev)}
                          isAnimationActive={false}
                          cursor="pointer"
                          onClick={(_data: unknown, index: number) => {
                            const entry = barData[index];
                            if (entry) handleSingleBarClick(sev, entry);
                          }}
                        >
                          {barData.map((entry) => {
                            const isDateSelected =
                              selectedBarDate === entry.date;
                            const isSevSelected = expandedSeverities.has(sev);
                            const nothingSelected =
                              selectedBarDate === null &&
                              expandedSeverities.size === 0;
                            const isActive =
                              nothingSelected ||
                              (isDateSelected && isSevSelected) ||
                              (!selectedBarDate && isSevSelected);
                            return (
                              <Cell
                                key={entry.date}
                                opacity={isActive ? 1 : 0.3}
                                style={{
                                  transform:
                                    isDateSelected && isSevSelected
                                      ? "scaleY(1.06)"
                                      : "scaleY(1)",
                                  transformOrigin: "bottom",
                                  transition:
                                    "transform 0.2s ease, opacity 0.2s ease",
                                }}
                              />
                            );
                          })}
                        </Bar>
                      ))}
                    </BarChart>
                  ) : (
                    <AreaChart data={filteredData}>
                      <defs>
                        {SEVERITIES.map((sev) => (
                          <linearGradient
                            key={sev}
                            id={`fill-${sev}`}
                            x1="0"
                            y1="0"
                            x2="0"
                            y2="1"
                          >
                            <stop
                              offset="5%"
                              stopColor={getSeverityColor(sev)}
                              stopOpacity={0.3}
                            />
                            <stop
                              offset="95%"
                              stopColor={getSeverityColor(sev)}
                              stopOpacity={0.02}
                            />
                          </linearGradient>
                        ))}
                      </defs>
                      <CartesianGrid
                        vertical={false}
                        strokeDasharray="3 3"
                        className="stroke-border"
                      />
                      <XAxis
                        dataKey="date"
                        tickLine={false}
                        axisLine={false}
                        tickMargin={isCompact ? 4 : 8}
                        minTickGap={isCompact ? 60 : 32}
                        className="text-muted"
                        tick={{ fontSize: isCompact ? 9 : 11 }}
                        tickFormatter={(value: string) => {
                          const date = new Date(value);
                          return date.toLocaleDateString("de-DE", {
                            month: "short",
                            day: "numeric",
                          });
                        }}
                      />
                      <YAxis
                        tickLine={false}
                        axisLine={false}
                        width={isCompact ? 24 : 32}
                        className="text-muted"
                        tick={{ fontSize: isCompact ? 9 : 11 }}
                        allowDecimals={false}
                      />
                      {!isCompact && (
                        <ChartTooltip
                          config={chartConfig}
                          labelFormatter={(value: string) =>
                            new Date(value).toLocaleDateString("de-DE", {
                              month: "short",
                              day: "numeric",
                              year: "numeric",
                            })
                          }
                        />
                      )}
                      {SEVERITIES.map((sev) => (
                        <Area
                          key={sev}
                          dataKey={sev}
                          type="natural"
                          fill={`url(#fill-${sev})`}
                          stroke={getSeverityColor(sev)}
                          strokeWidth={isCompact ? 2 : 2.5}
                          dot={false}
                          activeDot={false}
                          hide={isCompact && !expandedSeverities.has(sev)}
                        />
                      ))}
                      {/* Dots overlay */}
                      <Customized
                        component={(props: Record<string, unknown>) => {
                          const items = props.formattedGraphicalItems as
                            | Array<{
                                props: {
                                  points: Array<{ x: number; y: number }>;
                                };
                                item: { props: { dataKey: string } };
                              }>
                            | undefined;
                          if (!items) return <g />;

                          return (
                            <g className="dots-overlay">
                              <style>{`
                                .finding-dot .dot-visible {
                                  transition: transform 0.2s ease, opacity 0.15s ease;
                                }
                                .finding-dot:hover .dot-visible {
                                  transform: scale(2);
                                  opacity: 1 !important;
                                }
                                .finding-dot .dot-glow {
                                  opacity: 0;
                                  transition: opacity 0.2s ease;
                                }
                                .finding-dot:hover .dot-glow {
                                  opacity: 0.25;
                                }
                              `}</style>
                              {items.map((areaItem) => {
                                const sev = areaItem.item.props
                                  .dataKey as Severity;
                                if (!SEVERITIES.includes(sev)) return null;
                                if (isCompact && !expandedSeverities.has(sev))
                                  return null;
                                return areaItem.props.points.map(
                                  (point, idx) => {
                                    const dataPoint = filteredData[idx];
                                    if (!dataPoint) return null;
                                    const newForSev =
                                      dataPoint.newFindings?.filter(
                                        (f) => f.severity === sev,
                                      );
                                    if (!newForSev?.length) return null;
                                    const isHighlighted = newForSev.some(
                                      (f) => f._id === highlightedFindingId,
                                    );
                                    return (
                                      // biome-ignore lint/a11y/noStaticElementInteractions: SVG <g> cannot be a semantic element
                                      <g
                                        key={`dot-${sev}-${dataPoint.date}`}
                                        className="finding-dot"
                                        onClick={() =>
                                          handleDotClick(sev, newForSev[0]._id)
                                        }
                                        onMouseEnter={() =>
                                          setDotTooltip({
                                            x: point.x,
                                            y: point.y,
                                            findings: newForSev,
                                            severity: sev,
                                          })
                                        }
                                        onMouseLeave={() => setDotTooltip(null)}
                                        style={{ cursor: "pointer" }}
                                      >
                                        <circle
                                          cx={point.x}
                                          cy={point.y}
                                          r={12}
                                          fill="transparent"
                                        />
                                        <circle
                                          cx={point.x}
                                          cy={point.y}
                                          r={8}
                                          fill={getSeverityColor(sev)}
                                          className="dot-glow"
                                          style={{
                                            transformOrigin: `${point.x}px ${point.y}px`,
                                          }}
                                        />
                                        <circle
                                          cx={point.x}
                                          cy={point.y}
                                          r={isHighlighted ? 5 : 2.5}
                                          fill={getSeverityColor(sev)}
                                          stroke="var(--color-card, #fff)"
                                          strokeWidth={1.5}
                                          opacity={0.8}
                                          className="dot-visible"
                                          style={{
                                            transformOrigin: `${point.x}px ${point.y}px`,
                                          }}
                                        />
                                        {isHighlighted && (
                                          <circle
                                            cx={point.x}
                                            cy={point.y}
                                            r={8}
                                            fill="none"
                                            stroke={getSeverityColor(sev)}
                                            strokeWidth={1.5}
                                            opacity={0.4}
                                          />
                                        )}
                                      </g>
                                    );
                                  },
                                );
                              })}
                            </g>
                          );
                        }}
                      />
                    </AreaChart>
                  )}
                </ChartContainer>

                {/* Dot hover tooltip — line mode only */}
                {chartType === "line" && dotTooltip && (
                  <div
                    className="absolute z-10 pointer-events-none rounded-lg border border-border bg-card/95 backdrop-blur-sm px-2.5 py-1.5 shadow-sm transition-opacity"
                    style={{
                      left: dotTooltip.x,
                      top: dotTooltip.y - 8,
                      transform: "translate(-50%, -100%)",
                    }}
                  >
                    {dotTooltip.findings.map((f) => (
                      <div key={f._id} className="flex items-center gap-1.5">
                        <span
                          className="w-1.5 h-1.5 rounded-full shrink-0"
                          style={{
                            background: getSeverityColor(dotTooltip.severity),
                          }}
                        />
                        <span className="text-[11px] text-primary whitespace-nowrap max-w-[180px] truncate">
                          {f.title}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Legend — horizontal below (full) or vertical right (compact) */}
              <div className={isCompact ? "pb-6" : "w-full shrink-0"}>
                <SeverityLegend
                  compact={isCompact}
                  chartConfig={chartConfig}
                  expandedSeverities={expandedSeverities}
                  onToggle={toggleSeverity}
                  groupedData={filteredGroupedData}
                />
              </div>
            </div>
          </>
        )}
      </div>

      {/* Scrollable detail list — only takes space when content exists */}
      <div
        ref={scrollContainerRef}
        className={`overflow-y-auto min-h-0 px-4 pb-1 ${activeDetailGroups.length > 0 ? "flex-1" : ""}`}
      >
        <AnimatePresence>
          {activeDetailGroups.length > 0 && filteredGroupedData && (
            <motion.div
              key="detail"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.25, ease: "easeInOut" }}
              className="border-t border-border pt-3 overflow-hidden"
            >
              {/* Date header for bar mode */}
              {chartType === "bar" && selectedBarDate && (
                <div className="flex items-center justify-between pb-2">
                  <span className="text-xs font-semibold text-secondary">
                    {new Date(selectedBarDate).toLocaleDateString("de-DE", {
                      weekday: "short",
                      day: "numeric",
                      month: "long",
                      year: "numeric",
                    })}
                  </span>
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedBarDate(null);
                      setExpandedSeverities(new Set());
                    }}
                    className="text-xs text-muted hover:text-secondary transition-colors"
                  >
                    &times;
                  </button>
                </div>
              )}
              {activeDetailGroups.map((sev) => {
                // In bar mode, use barSelectedFindings (handles both date-specific and legend-click)
                const group =
                  chartType === "bar"
                    ? barSelectedFindings?.find((g) => g.severity === sev)
                    : filteredGroupedData.groups.find(
                        (g) => g.severity === sev,
                      );
                if (!group) return null;
                const findings = group.findings;
                return (
                  <div key={sev}>
                    <div className="flex items-center gap-1.5 text-xs font-semibold text-secondary pb-1.5">
                      <span
                        className="w-2 h-2 rounded-full"
                        style={{ background: getSeverityColor(sev) }}
                      />
                      {chartConfig[sev].label} ({findings.length})
                    </div>
                    {findings.length === 0 ? (
                      <div className="text-xs text-muted py-2 pl-4">
                        {t("dashboard:findingsSeverity.empty")}
                      </div>
                    ) : (
                      <div className="flex flex-col gap-1 mb-3">
                        {findings.map((finding) => (
                          <FindingRow
                            key={finding._id}
                            finding={finding}
                            workspaceSlug={workspaceSlug}
                            isHighlighted={finding._id === highlightedFindingId}
                          />
                        ))}
                        {group.total > 10 && (
                          <div className="text-center pt-1">
                            <span className="text-xs text-accent font-medium cursor-pointer hover:underline">
                              {t("dashboard:findingsSeverity.showAll")}
                            </span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Summary Strip — pinned at bottom */}
      {filteredGroupedData && (
        <motion.div layout transition={{ duration: 0.25, ease: "easeInOut" }}>
          <SummaryStrip totals={filteredGroupedData.totals} />
        </motion.div>
      )}
    </div>
  );
}

const SCOPE_OPTIONS: AuditScope[] = ["active", "all", "completed"];

function ScopeFilterPopover({
  scope,
  onScopeChange,
}: {
  scope: AuditScope;
  onScopeChange: (scope: AuditScope) => void;
}) {
  const { t } = useTranslation(["dashboard"]);

  return (
    <Popover.Root>
      <Popover.Trigger asChild>
        <button
          type="button"
          className="flex h-8 w-8 items-center justify-center text-muted hover:text-primary transition-colors"
          title={t("dashboard:findingsSeverity.scope.active")}
        >
          <SlidersHorizontal size={16} />
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          side="bottom"
          align="end"
          sideOffset={8}
          className="z-50 w-52 rounded-xl border border-border bg-card p-2 shadow-lg"
        >
          {SCOPE_OPTIONS.map((option) => {
            const isActive = scope === option;
            return (
              <Popover.Close key={option} asChild>
                <button
                  type="button"
                  onClick={() => onScopeChange(option)}
                  className={`flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm transition-colors ${
                    isActive
                      ? "bg-surface text-primary font-medium"
                      : "text-secondary hover:bg-surface"
                  }`}
                >
                  <span className="flex-1 text-left">
                    {t(
                      `dashboard:findingsSeverity.scope.${option}` as "dashboard:findingsSeverity.scope.active",
                    )}
                  </span>
                  {isActive && <Check size={14} className="text-accent" />}
                </button>
              </Popover.Close>
            );
          })}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

function SeverityLegend({
  compact,
  chartConfig,
  expandedSeverities,
  onToggle,
  groupedData,
}: {
  compact: boolean;
  chartConfig: ChartConfig;
  expandedSeverities: Set<string>;
  onToggle: (severity: string) => void;
  groupedData?: {
    groups: { severity: string; total: number }[];
  };
}) {
  return (
    <div
      className={
        compact
          ? "flex flex-col gap-1"
          : "flex flex-wrap items-center justify-center gap-x-4 gap-y-1 pt-1"
      }
    >
      {SEVERITIES.map((sev) => {
        const isActive = expandedSeverities.has(sev);
        const count = groupedData?.groups.find(
          (g) => g.severity === sev,
        )?.total;
        return (
          <button
            key={sev}
            type="button"
            onClick={() => onToggle(sev)}
            className="flex items-center gap-1.5 cursor-pointer hover:opacity-80 transition-opacity outline-none"
          >
            <span
              className="w-2.5 h-2.5 rounded-full shrink-0"
              style={{ backgroundColor: getSeverityColor(sev) }}
            />
            <span
              className={`text-xs whitespace-nowrap transition-colors ${
                isActive ? "text-primary font-semibold" : "text-secondary"
              }`}
            >
              {chartConfig[sev].label}
              {count !== undefined ? ` (${count})` : ""}
            </span>
          </button>
        );
      })}
    </div>
  );
}

function SummaryStrip({
  totals,
}: {
  totals: { total: number; open: number; draft: number; resolved: number };
}) {
  const { t } = useTranslation(["dashboard"]);
  return (
    <div className="flex items-center justify-center gap-4 py-1.5 border-t border-border mx-4 shrink-0 text-xs">
      <span className="font-semibold text-primary">
        {totals.total} {t("dashboard:findingsSeverity.total")}
      </span>
      <span
        className="font-medium"
        style={{ color: getPaletteColor("deadlines", 1) }}
      >
        {totals.open} {t("dashboard:findingsSeverity.status.open")}
      </span>
      <span
        className="font-medium"
        style={{ color: getPaletteColor("audit-status", 6) }}
      >
        {totals.draft} {t("dashboard:findingsSeverity.status.draft")}
      </span>
      <span
        className="font-medium"
        style={{ color: getPaletteColor("deadlines", 3) }}
      >
        {totals.resolved} {t("dashboard:findingsSeverity.status.resolved")}
      </span>
    </div>
  );
}
